/**
 * Materialliste und Kalkulation (Kapitel J).
 *
 * Klare Ansage aus dem Gespraech: Bisher wird jede Schraube einzeln
 * geschrieben. Fuer das automatisierte Angebot wird pauschal gerechnet — pro
 * Modul ein Satz Anbauteile, abhaengig von Dachtyp und Eindeckung.
 *
 * Die Detailtiefe bleibt intern verfuegbar. Der Unterschied liegt nur darin,
 * was im Angebot beim Kunden erscheint: dieselbe Datenbasis, zwei Sichten
 * (PV-J03).
 *
 * Zwei Dinge macht dieses Modul bewusst NICHT:
 *   - Es schaetzt keine Preise. Eine Position ohne hinterlegten Preis bleibt
 *     ohne Betrag und wird als solche ausgewiesen. „Auf Anfrage" als Nullpreis
 *     in ein Kundenangebot zu schreiben waere der teuerste Fehler hier.
 *   - Es rechnet keine Ballast- und Windlast (in der Spec ausdruecklich
 *     verneint). Ballast ist eine Position, kein Rechenweg.
 */
import { montagemengen } from './montage.js';
export const DEFAULT_KALKULATION = {
    kabelzuschlagProzent: 15,
    stundenJeModul: 0.35,
    grundstunden: 8,
    aufschlaege: {},
};
const WARENGRUPPENNAMEN = {
    module: 'Module',
    wechselrichter: 'Wechselrichter',
    speicher: 'Speicher',
    montagematerial: 'Montagematerial',
    elektromaterial: 'Elektroinstallation',
    arbeit: 'Montage',
    sonstiges: 'Sonstiges',
};
export function rechneMaterial(eingabe) {
    const regeln = eingabe.regeln ?? DEFAULT_KALKULATION;
    const positionen = [];
    const fehlendeMaterialsaetze = [];
    const ausArtikel = (artikelId, menge, ersatzbezeichnung) => {
        const artikel = eingabe.artikel.get(artikelId);
        return {
            artikelId,
            bezeichnung: artikel?.bezeichnung ?? ersatzbezeichnung ?? `Unbekannter Artikel ${artikelId}`,
            einheit: artikel?.einheit ?? 'Stk',
            menge,
            ...(artikel?.einkaufspreis != null ? { einzelpreis: artikel.einkaufspreis } : {}),
            warengruppe: artikel?.warengruppe ?? 'sonstiges',
            herkunft: 'gerechnet',
        };
    };
    // --- Module und Montagematerial je Flaeche -------------------------------
    let moduleGesamt = 0;
    for (const flaeche of eingabe.flaechen) {
        if (flaeche.anzahlModule === 0)
            continue;
        moduleGesamt += flaeche.anzahlModule;
        if (flaeche.modulArtikelId) {
            positionen.push(ausArtikel(flaeche.modulArtikelId, flaeche.anzahlModule));
        }
        const satz = eingabe.materialsaetze.find((s) => s.dachtyp === flaeche.dachtyp && s.ausrichtung === flaeche.ausrichtung);
        if (!satz) {
            const fehlt = `${flaeche.dachtyp || 'ohne Dachtyp'} / ${flaeche.ausrichtung}`;
            if (!fehlendeMaterialsaetze.includes(fehlt))
                fehlendeMaterialsaetze.push(fehlt);
            continue;
        }
        for (const zeile of satz.jeModul) {
            positionen.push(ausArtikel(zeile.artikelId, runde(zeile.menge * flaeche.anzahlModule)));
        }
        for (const zeile of satz.jeFlaeche) {
            positionen.push(ausArtikel(zeile.artikelId, zeile.menge));
        }
        if (satz.montage && flaeche.straenge) {
            for (const zeile of montagemengen(flaeche.straenge, satz.montage, flaeche.traeger)) {
                positionen.push(ausArtikel(zeile.artikelId, zeile.menge));
            }
        }
    }
    // --- Komponenten ---------------------------------------------------------
    for (const komponente of eingabe.komponenten) {
        positionen.push({
            ...(komponente.artikelId ? { artikelId: komponente.artikelId } : {}),
            bezeichnung: komponente.bezeichnung,
            einheit: 'Stk',
            menge: komponente.anzahl,
            ...(komponente.einzelpreis != null ? { einzelpreis: komponente.einzelpreis } : {}),
            warengruppe: komponente.warengruppe,
            herkunft: 'gerechnet',
        });
    }
    for (const zeile of eingabe.stueckgut ?? []) {
        if (zeile.menge > 0)
            positionen.push(ausArtikel(zeile.artikelId, zeile.menge));
    }
    // --- Leitungen mit Verlegezuschlag (PV-J04) ------------------------------
    for (const leitung of eingabe.leitungen) {
        const menge = runde(leitung.laengeM * (1 + regeln.kabelzuschlagProzent / 100));
        if (leitung.artikelId) {
            positionen.push(ausArtikel(leitung.artikelId, menge, leitung.bezeichnung));
        }
        else {
            positionen.push({
                bezeichnung: leitung.bezeichnung,
                einheit: 'm',
                menge,
                warengruppe: 'elektromaterial',
                herkunft: 'gerechnet',
            });
        }
    }
    // --- Montagestunden (PV-J05) ---------------------------------------------
    if (moduleGesamt > 0) {
        const stunden = runde(regeln.grundstunden + regeln.stundenJeModul * moduleGesamt);
        positionen.push({
            bezeichnung: 'Montage',
            einheit: 'h',
            menge: stunden,
            ...(regeln.stundensatz != null ? { einzelpreis: regeln.stundensatz } : {}),
            warengruppe: 'arbeit',
            herkunft: 'gerechnet',
        });
    }
    const alle = [...positionen, ...(eingabe.manuellePositionen ?? [])];
    const summeEinkauf = alle.reduce((summe, p) => summe + (p.einzelpreis != null ? p.einzelpreis * p.menge : 0), 0);
    const summeVerkauf = alle.reduce((summe, p) => summe + verkaufswert(p, regeln.aufschlaege), 0);
    return {
        detailliert: alle,
        pauschal: fasseZusammen(alle, regeln.aufschlaege),
        summeEinkauf: runde(summeEinkauf, 2),
        summeVerkauf: runde(summeVerkauf, 2),
        ohnePreis: alle.filter((p) => p.einzelpreis == null),
        fehlendeMaterialsaetze,
    };
}
function verkaufswert(p, aufschlaege) {
    if (p.einzelpreis == null)
        return 0;
    const aufschlag = aufschlaege[p.warengruppe] ?? 0;
    return p.einzelpreis * p.menge * (1 + aufschlag / 100);
}
/**
 * Fuer das Kundenangebot: eine Zeile je Warengruppe statt hundert Einzelteile.
 * Positionen ohne Preis werden mitgezaehlt, aber nicht bepreist — der Betrag
 * fehlt dann sichtbar, statt still auf null zu stehen.
 */
function fasseZusammen(positionen, aufschlaege) {
    const gruppen = new Map();
    for (const p of positionen) {
        const bisher = gruppen.get(p.warengruppe) ?? { betrag: 0, vollstaendig: true };
        gruppen.set(p.warengruppe, {
            betrag: bisher.betrag + verkaufswert(p, aufschlaege),
            vollstaendig: bisher.vollstaendig && p.einzelpreis != null,
        });
    }
    return [...gruppen.entries()].map(([warengruppe, wert]) => ({
        bezeichnung: WARENGRUPPENNAMEN[warengruppe],
        einheit: 'Pauschale',
        menge: 1,
        ...(wert.vollstaendig ? { einzelpreis: runde(wert.betrag, 2) } : {}),
        warengruppe,
        herkunft: 'gerechnet',
    }));
}
const runde = (wert, stellen = 3) => Math.round(wert * 10 ** stellen) / 10 ** stellen;
export const warengruppenname = (gruppe) => WARENGRUPPENNAMEN[gruppe];
//# sourceMappingURL=material.js.map